import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  designDeployPatch,
  designDestroyPatch,
} from '../../../config/designFixtures';
import { isDesignCanvasProject } from '../../../config/designMode';
import { useAuth } from '../../../contexts/Auth';
import useApi from '../../../hooks/useAPI';

/**
 * @param {string} nodeId
 * @param {function} updateNodeData - local merge from useNodeData
 * @param {function|undefined} syncCanvas - optional data.updateNodeData from React Flow
 */
const useNodeDeployment = (nodeId, updateNodeData, syncCanvas) => {
  const [error, setError] = useState(null);
  const { projectId } = useParams();
  const { token } = useAuth();
  const { projects: projectsApi } = useApi();

  const patchDeploy = useCallback(
    (patch) => {
      updateNodeData((prev) => ({ ...prev, ...patch }));
      if (typeof syncCanvas === 'function' && patch && typeof patch === 'object') {
        syncCanvas(patch);
      }
    },
    [updateNodeData, syncCanvas]
  );

  const handleDeploy = useCallback(async () => {
    setError(null);
    patchDeploy({ deploy_status: 'DEPLOYING' });
    try {
      if (isDesignCanvasProject(projectId)) {
        updateNodeData((prev) => {
          const patch = designDeployPatch(prev);
          if (typeof syncCanvas === 'function') {
            queueMicrotask(() => syncCanvas(patch));
          }
          return patch;
        });
        return;
      }
      const response = await projectsApi.deployPackage(projectId, nodeId, token);
      const body = response?.body;
      if (body?.deploy_status) {
        patchDeploy({ deploy_status: body.deploy_status });
      }
    } catch (err) {
      console.error('Deployment failed:', err);
      patchDeploy({ deploy_status: 'FAILED' });
      setError(
        'Deployment failed. Please check the logs for more information.'
      );
    }
  }, [projectId, nodeId, projectsApi, token, patchDeploy, updateNodeData, syncCanvas]);

  const handleRun = useCallback(async () => {
    setError(null);
    try {
      // Status stays on package WS — run-until only flips the leaf when its job starts.
      await projectsApi.runUntil(projectId, nodeId, token);
    } catch (err) {
      console.error('Run failed:', err);
      setError(formatRunUntilError(err));
    }
  }, [projectId, nodeId, projectsApi, token]);

  const handleDestroy = useCallback(async () => {
    setError(null);
    patchDeploy({ deploy_status: 'DESTROYING' });
    try {
      if (isDesignCanvasProject(projectId)) {
        patchDeploy(designDestroyPatch());
        return;
      }
      const response = await projectsApi.destroyPackage(projectId, nodeId, token);
      const body = response?.body;
      if (body?.deploy_status) {
        patchDeploy({ deploy_status: body.deploy_status });
      }
    } catch (err) {
      console.error('Destruction failed:', err);
      patchDeploy({ deploy_status: 'FAILED' });
      setError(
        'Destruction failed. Please check the logs for more information.'
      );
    }
  }, [projectId, nodeId, projectsApi, token, patchDeploy]);

  return { error, handleDeploy, handleDestroy, handleRun };
};

function formatRunUntilError(err) {
  const data = err?.response?.data;
  const detail = typeof data?.detail === 'string' ? data.detail.trim() : '';
  const packageId = data?.package_id;
  if (detail && packageId && !detail.includes(String(packageId))) {
    return `${detail} (${packageId})`;
  }
  if (detail) {
    return detail;
  }
  return 'Run failed. Please check the logs for more information.';
}

export default useNodeDeployment;
